/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { useParams } from 'js/lib/useRouter';

import { Button, Grid, Hidden, Link, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type {
  CarouselCollection,
  CoreCollectionEntityWithMetadata,
} from 'bundles/collections-carousel/types/CarouselCollection';
import { mapCollectionEntityForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import CollectionDescription from 'bundles/enterprise-collections/components/CollectionDescription';
import CollectionModule from 'bundles/page-config-common/components/shared/CollectionModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import type { PageConfig_ComponentConfig_PageConfig_EnterpriseCuratedCollectionComponent_Fragment as CollectionComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useIsWildcard } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useCampusWordingForLearner } from 'bundles/unified-common/hooks/useCampusWordingForLearner';
import { CURATED_COLLECTION_MAX_LIMIT, CURATED_COLLECTION_MIN_LIMIT } from 'bundles/unified-home-common/constants';

import _t from 'i18n!nls/consumer-home';

const MAX_COLLECTION_ENTITY_LIMIT = 20;

const styles = {
  enterpriseCollectionHeader: css`
    margin-top: var(--cds-spacing-150);
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--cds-spacing-150);
  `,
  mobileButton: css`
    padding-top: var(--cds-spacing-100);
  `,
  collectionSpacing: css`
    gap: var(--cds-spacing-400);

    ${breakpoints.down('sm')} {
      gap: var(--cds-spacing-300);
    }
  `,
  collectionTitle: css`
    ${breakpoints.down('sm')} {
      /* stylelint-disable-next-line cds-stylelint/cds-typography-utility */
      font: var(--cds-typography-subtitle-medium);
    }
  `,
};

const EnterpriseCuratedCollectionModuleRenderer = ({
  pageConfigComponent,
}: {
  pageConfigComponent: CollectionComponent;
}) => {
  const { programSlug } = useParams();
  const features = useEnterpriseProductFeatures();
  const isWildcard = useIsWildcard();
  const { currentLevel, nextLevel } = useHeaderLevel();
  const isCampusWording = useCampusWordingForLearner();
  const thirdPartyOrg = useThirdPartyOrganization();
  const track = useTracker();

  // We want to show more collections if the program has disabled browse collections.
  const collectionsToShow =
    features?.programHideCourseraRecommendations || !isWildcard
      ? CURATED_COLLECTION_MAX_LIMIT
      : CURATED_COLLECTION_MIN_LIMIT;

  const carouselCollections = useMemo(() => {
    return pageConfigComponent.collection?.curatedCollectionEntities?.elements
      ?.slice(0, collectionsToShow)
      ?.map((maybeCollection, index) => {
        if (maybeCollection?.__typename === 'Collection_Collection') {
          return mapCollectionEntityForCarousel(
            maybeCollection.id,
            {
              ...maybeCollection,
              entities: maybeCollection.curatedCollectionProductEntities,
              metadata: {
                isEnterprise: true,
                programSlug,
                useC4cWordingForCreditBadge: isCampusWording,
                titleComponent: nextLevel,
                enterpriseCollectionTrackingId: maybeCollection.collectionTrackingId,
              },
            } as CoreCollectionEntityWithMetadata,
            {
              collectionIndex: index,
            }
          );
        } else {
          return null;
        }
      })
      ?.filter(Boolean);
  }, [pageConfigComponent, programSlug, collectionsToShow, nextLevel, isCampusWording]);
  const collectionSubtitles = useMemo(() => {
    return pageConfigComponent.collection?.curatedCollectionEntities?.elements
      ?.slice(0, collectionsToShow)
      .reduce((acc, maybeCollection) => {
        if (maybeCollection?.__typename === 'Collection_Collection') {
          return {
            ...acc,
            [maybeCollection.id]: {
              cmlValue: maybeCollection.subtitle?.cmlValue,
              dtdId: maybeCollection.subtitle?.dtdId,
            },
          };
        } else {
          return acc;
        }
      }, {} as { [key: string]: { cmlValue?: string; dtdId?: string } });
  }, [pageConfigComponent, collectionsToShow]);
  // We cap the amount of entities in a collection at MAX_COLLECTION_ENTITY_LIMIT. If we hit that limit in a collection,
  // we want to display the link to the view all collections page.
  const collectionMaxLimitHit = useMemo(() => {
    return carouselCollections?.some((collection) => collection.entities.length >= MAX_COLLECTION_ENTITY_LIMIT);
  }, [carouselCollections]);

  if (!carouselCollections) {
    return null;
  }

  const hasMoreCollectionsThanDisplayed =
    (pageConfigComponent?.collection?.curatedCollectionEntities?.pagination?.totalElements || 0) > collectionsToShow;
  const showViewAllButton = hasMoreCollectionsThanDisplayed || collectionMaxLimitHit;

  const onViewAllClick = () => {
    track('click_button', {
      button: {
        name: 'view_all_products',
        linkName: _t('View all collections'),
        linkType: 'internal',
        linkURL: `/programs/${programSlug}/view-all-collections/curated`,
      },
    });
  };

  if (!carouselCollections?.length) {
    return null;
  }

  const renderenterpriseHomeRefreshHeader = (collection: CarouselCollection, index: number) => (
    <div>
      {index === 0 && (
        <div css={styles.header}>
          <Typography2 variant={currentLevel === 'h2' ? 'titleSmall' : 'titleXSmall'} component={currentLevel}>
            {_t('Collections selected by #{orgName}', { orgName: thirdPartyOrg?.name || _t('your organization') })}
          </Typography2>
          {!!showViewAllButton && (
            <Hidden xsDown>
              <Button
                variant="ghost"
                size="small"
                onClick={onViewAllClick}
                component={Link}
                edgeAlign="end"
                href={`/programs/${programSlug}/view-all-collections/curated`}
              >
                {_t('View all collections')}
              </Button>
            </Hidden>
          )}
        </div>
      )}
      <Typography2 variant="subtitleLarge" component={currentLevel} css={styles.collectionTitle}>
        {collection.title}
      </Typography2>
      {!!collectionSubtitles?.[collection.collectionId] && (
        <CollectionDescription
          collection={{
            description: {
              cml: {
                // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                value: collectionSubtitles[collection.collectionId].cmlValue || '',
                // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                dtdId: collectionSubtitles[collection.collectionId].dtdId || '',
              },
            },
          }}
        />
      )}
    </div>
  );

  return (
    <HomeSection
      className="PageConfig_EnterpriseCuratedCollectionComponent"
      dataTestId="enterprise-curated-collections"
    >
      <Grid container css={styles.collectionSpacing}>
        {carouselCollections?.map((collection, index) => (
          <Grid item xs={12} key={collection.collectionId}>
            <CollectionModule
              collection={collection}
              disableNegativeMargins={true}
              disableCollectionPadding={true}
              customHeader={renderenterpriseHomeRefreshHeader(collection, index)}
            />
          </Grid>
        ))}
      </Grid>
      {!!showViewAllButton && (
        <Hidden smUp css={styles.mobileButton}>
          <Button
            variant="ghost"
            size="small"
            fullWidth
            onClick={onViewAllClick}
            component="a"
            href={`/programs/${programSlug}/view-all-collections/curated`}
          >
            {_t('View all collections')}
          </Button>
        </Hidden>
      )}
    </HomeSection>
  );
};

export default EnterpriseCuratedCollectionModuleRenderer;
